<template>
  <div class="custom-multi-select" :style="scssProps" v-closable="{ exclude: [], handler: 'onFocusOut' }">
    <b-input-group size="sm" @click="onclick" ref="inputContainer">
      <template v-slot:append>
        <template v-if="count > 0">
          <b-input-group-text v-if="showCount" class="infoBox pr-1">
            <b-badge pill class="myBadge">{{ count }}</b-badge>
          </b-input-group-text>
          <b-input-group-text
            v-if="showClearSelection"
            @click.prevent.stop="deleteAll()"
            class="font-weight-bold infoBox"
            ><font-awesome-icon :icon="['fas', 'times']"
          /></b-input-group-text>
        </template>
        <b-input-group-text v-show="!isOpen" class="infoBox infoBoxLast pl-3"
          ><font-awesome-icon :icon="['fas', 'chevron-down']"
        /></b-input-group-text>
        <b-input-group-text v-show="isOpen" class="infoBox infoBoxLast pl-3"
          ><font-awesome-icon :icon="['fas', 'chevron-up']"
        /></b-input-group-text>
      </template>
      <b-input :value="textValue" autocomplete="off" class="custom-select-sm" :disabled="disabled" readonly> </b-input>
    </b-input-group>
    <div v-show="isOpen" class="options-list" ref="optionsList" :style="'width: ' + calculatedWidth + 'px;'">
      <div class="options-container">
        <slot name="beforeSearchInput"></slot>
        <div class="search" v-if="innerOptions.length > 10">
          <b-input-group class="searchBox">
            <b-input-group-text>
              <i class="fas fa-search"></i>
            </b-input-group-text>
            <b-input
              v-model="search"
              :placeholder="placeholder"
              size="sm"
              autocomplete="off"
              data-test="customSelectSearch"
            ></b-input>
          </b-input-group>
        </div>
        <div class="options text-left">
          <ul class="list-unstyled">
            <li v-if="showAll && search === ''" @click="selectAll()">
              <span v-if="multiple && useTicks" v-visible="_value.length === 0"><i class="fas fa-check"></i></span>
              <i>{{ selectAllText }}</i>
            </li>

            <li
              v-for="option in searchOptions"
              :key="option.value"
              :class="!useTicks && value.includes(option.value) ? 'font-weight-bold' : ''"
              @click="toggle(option)"
            >
              <span v-if="multiple && useTicks" v-visible="_value.includes(option.value)">
                <i class="fas fa-check"></i>
              </span>
              <template v-if="$slots['item-option'] || $scopedSlots['item-option']">
                <slot name="item-option" :option="option"></slot>
              </template>
              <template v-else>
                {{ option.text }}
              </template>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
let handleOutsideClick = null
export default {
  name: 'CustomMultiSelect',
  props: {
    value: Array,
    options: {},
    maintainOriginalOrder: Boolean,
    disabled: Boolean,
    defaultText: String,
    selectAllText: String,
    multiple: Boolean,
    showAll: Boolean,
    showClearSelection: Boolean,
    showCount: Boolean,
    placeholder: { type: String, default: 'Search' },
    variant: { type: String, default: 'blue' },
    useTicks: Boolean,
  },
  data() {
    return {
      isOpen: false,
      search: '',
      calculatedWidth: 100,
    }
  },
  methods: {
    onclick() {
      if (this.disabled) return
      this.isOpen = !this.isOpen
    },
    onFocusOut: function () {
      this.search = ''
      this.isOpen = false
      this.$emit('blur')
    },
    toggle(clicked) {
      if (this.disabled) return
      if (!this.multiple) {
        this.innerOptions.forEach((el) => (el.selected = false))
        this.isOpen = false
      }
      clicked.selected = !clicked.selected
      this.$emit(
        'input',
        this.innerOptions.filter((el) => el.selected).map((el) => el.value)
      )
    },
    selectAll() {
      if (this.disabled) return
      this.innerOptions.forEach((el) => (el.selected = false))
      this.$emit('input', [])
    },
    deleteAll() {
      if (this.disabled) return
      this.innerOptions.forEach((el) => (el.selected = false))
      this.$emit('input', [])
    },
  },
  computed: {
    scssProps() {
      return {
        '--hoverVariant': this.variant === 'blue' ? 'var(--blueVariant)' : 'var(--gray4)',
        '--bgVariant': this.variant === 'blue' ? 'var(--secondaryBGColor)' : 'var(--whiteBgColor)',
      }
    },

    _value() {
      let result = this.value ? this.value.map((el) => (el = el.toString())) : []
      return result
    },
    innerOptions() {
      let tempOpts = this.options
      let val = this._value

      if (!(tempOpts instanceof Array)) {
        tempOpts = Object.keys(tempOpts).map((i) => {
          return { value: i, text: tempOpts[i], selected: val.includes(i) }
        })
      } else {
        tempOpts = tempOpts.map((value, index) => {
          if (value.value !== undefined && value.text !== undefined) {
            return { value: value.value, text: value.text, selected: val.includes(value.value) }
          } else {
            return { value: index, text: value, selected: val.includes(index) }
          }
        })
      }
      if (!this.maintainOriginalOrder) {
        tempOpts = tempOpts.sort((a, b) => (a.text > b.text ? 1 : -1))
      }
      return tempOpts.sort((a, b) => {
        if (a.text === 'N/D' || a.text === 'N/A') {
          return 1
        }
        if (b.text === 'N/D' || b.text === 'N/A') {
          return -1
        }
        return 0
      })
    },
    searchOptions() {
      if (this.search === '') {
        return this.innerOptions
      } else {
        return this.innerOptions.filter((el) => {
          const regex = new RegExp(this.search, 'gi')
          return el.text.match(regex)
        })
      }
    },
    textValue() {
      let temp = this._value
      temp = temp.filter((el) => this.innerOptions.find((el2) => el2.value === el))
      return temp.map((el) => this.innerOptions.find((el2) => el2.value === el).text).join(', ') || this.defaultText
    },
    selected() {
      return this.innerOptions.filter((el) => el.selected)
    },
    count() {
      return this.selected.length
    },
  },
  watch: {
    isOpen: function () {
      this.calculatedWidth = this.$refs.inputContainer.clientWidth
    },
  },
  mounted: function () {
    this.$emit('enterpressed', this.textValue)
  },
  directives: {
    closable: {
      bind(el, binding, vnode) {
        handleOutsideClick = (e) => {
          e.stopPropagation()
          const { handler, exclude } = binding.value
          let clickedOnExcludedEl = false
          exclude.forEach((refName) => {
            if (!clickedOnExcludedEl) {
              const excludedEl = vnode.context.$refs[refName]
              clickedOnExcludedEl = excludedEl.contains(e.target)
            }
          })
          if (!el.contains(e.target) && !clickedOnExcludedEl) {
            vnode.context[handler]()
          }
        }
        document.addEventListener('click', handleOutsideClick)
        document.addEventListener('touchstart', handleOutsideClick)
      },

      unbind() {
        document.removeEventListener('click', handleOutsideClick)
        document.removeEventListener('touchstart', handleOutsideClick)
      },
    },
  },
}
</script>
<style lang="scss" scoped>
.myBadge {
  background-color: #779ac9;
}

.custom-select-sm {
  border-right: 0;
}

.custom-multi-select input,
.custom-multi-select .infoBox,
.custom-multi-select .searchBox *,
.custom-multi-select .options-container {
  background-color: var(--bgVariant) !important;
}

.custom-multi-select .options-list .options ul li:hover {
  background-color: var(--hoverVariant) !important;
}

.infoBox {
  border-left: 0;
}

.infoBoxLast {
  border-bottom-right-radius: 0.25rem !important;
  border-top-right-radius: 0.25rem !important;
}

.searchBox * {
  margin: -1px;
}

.searchBox input {
  border-left: 0;
}

.searchBox .input-group-text {
  border-right: 0;
}

.custom-multi-select {
  .custom-select-active {
    color: var(--primary);
    font-weight: 700;
  }

  input {
    &:read-only {
      background-color: #ffffff;
    }
  }

  .search {
    padding: 0.2rem;
  }

  .options-list {
    position: absolute;
    z-index: 10;
    background-color: #ffffff;
    border: 1px solid #ced4da;
    padding: 0;
    color: #495057;
    font-size: 0.9rem;

    .options {
      height: auto;
      max-height: 300px;
      overflow-y: auto;
      overflow-x: hidden;

      ul {
        margin-bottom: 0;

        li {
          cursor: default;
          padding: 0.25rem 0.75rem;
          white-space: nowrap;

          &:hover {
            color: #000000;
          }
        }
      }
    }
  }
}
</style>
